import { useState, useEffect, useRef, useCallback } from "react";
import { Music, Volume2, Volume1, VolumeX, Play } from "lucide-react";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";

// Bump TRACK_VERSION whenever the MP3 is replaced so browsers don't play a cached old track
const TRACK_VERSION = "2026-06-alla-turca-looped-v2";
const TRACK_SRC = `/audio/lobby-loop.mp3?v=${TRACK_VERSION}`;
const VOLUME_KEY = "live_lobby_music_volume";
const MUTED_KEY = "live_lobby_music_muted";
const FADE_SECONDS = 0.5;
const SILENCE = 0.002; // ~-54 dB: anything quieter is treated as encoder padding

const load = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : JSON.parse(v);
  } catch {
    return fallback;
  }
};

// MP3 encoders pad the start/end with silence; find the real audio boundaries so the loop is seamless
const findLoopPoints = (buffer) => {
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i));
  const n = buffer.length;
  let first = 0;
  while (first < n && channels.every((c) => Math.abs(c[first]) < SILENCE)) first += 1;
  let last = n - 1;
  while (last > first && channels.every((c) => Math.abs(c[last]) < SILENCE)) last -= 1;
  return { loopStart: first / buffer.sampleRate, loopEnd: (last + 1) / buffer.sampleRate };
};

export const LobbyMusic = ({ active }) => {
  const ctxRef = useRef(null);
  const gainRef = useRef(null);
  const bufferRef = useRef(null);
  const sourceRef = useRef(null);
  const loopRef = useRef({ loopStart: 0, loopEnd: 0 });
  const [volume, setVolume] = useState(() => load(VOLUME_KEY, 0.5));
  const [muted, setMuted] = useState(() => load(MUTED_KEY, false));
  const [blocked, setBlocked] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    ctxRef.current = ctx;
    gainRef.current = gain;
    let cancelled = false;
    fetch(TRACK_SRC)
      .then((r) => r.arrayBuffer())
      .then((bytes) => ctx.decodeAudioData(bytes))
      .then((buffer) => {
        if (cancelled) return;
        bufferRef.current = buffer;
        loopRef.current = findLoopPoints(buffer);
        setReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      try { sourceRef.current?.stop(); } catch { /* already stopped */ }
      ctx.close().catch(() => {});
    };
  }, []);

  useEffect(() => {
    const gain = gainRef.current;
    if (gain && sourceRef.current) gain.gain.setTargetAtTime(muted ? 0 : volume, ctxRef.current.currentTime, 0.02);
    localStorage.setItem(VOLUME_KEY, JSON.stringify(volume));
    localStorage.setItem(MUTED_KEY, JSON.stringify(muted));
  }, [volume, muted]);

  const stopSource = useCallback(() => {
    const src = sourceRef.current;
    sourceRef.current = null;
    if (!src) return;
    try { src.stop(); } catch { /* already stopped */ }
    src.disconnect();
  }, []);

  const tryPlay = useCallback(() => {
    const ctx = ctxRef.current;
    const buffer = bufferRef.current;
    if (!ctx || !buffer || sourceRef.current) return;
    ctx.resume().then(() => {
      if (ctx.state !== "running") { setBlocked(true); return; }
      const { loopStart, loopEnd } = loopRef.current;
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.loopStart = loopStart;
      src.loopEnd = loopEnd;
      src.connect(gainRef.current);
      gainRef.current.gain.cancelScheduledValues(ctx.currentTime);
      gainRef.current.gain.setValueAtTime(muted ? 0 : volume, ctx.currentTime);
      src.start(0, loopStart);
      sourceRef.current = src;
      setBlocked(false);
    }).catch(() => setBlocked(true));
  }, [volume, muted]);

  const fadeOut = useCallback(() => {
    const ctx = ctxRef.current;
    const gain = gainRef.current;
    const src = sourceRef.current;
    if (!ctx || !gain || !src) return;
    gain.gain.cancelScheduledValues(ctx.currentTime);
    gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + FADE_SECONDS);
    sourceRef.current = null;
    setTimeout(() => {
      try { src.stop(); } catch { /* already stopped */ }
      src.disconnect();
    }, FADE_SECONDS * 1000 + 50);
  }, []);

  useEffect(() => {
    if (active && ready) tryPlay();
    else if (!active) {
      fadeOut();
      setBlocked(false);
    }
  }, [active, ready, tryPlay, fadeOut]);

  useEffect(() => () => stopSource(), [stopSource]);

  // Browser autoplay policy blocks audio without a gesture (e.g. after a page refresh)
  useEffect(() => {
    if (!blocked || !active) return;
    const resume = () => tryPlay();
    window.addEventListener("pointerdown", resume, { once: true });
    window.addEventListener("keydown", resume, { once: true });
    return () => {
      window.removeEventListener("pointerdown", resume);
      window.removeEventListener("keydown", resume);
    };
  }, [blocked, active, tryPlay]);

  if (!active) return null;

  const silent = muted || volume === 0;
  const Icon = silent ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <div
      className="flex items-center gap-1.5 rounded-full border border-border bg-card pl-3 pr-2 py-1"
      data-testid="lobby-music-controls"
      data-loop-start={loopRef.current.loopStart}
      data-loop-end={loopRef.current.loopEnd}
    >
      <Music className="w-4 h-4 text-muted-foreground hidden sm:block" />
      {blocked ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 rounded-full gap-1.5 text-primary"
          onClick={tryPlay}
          data-testid="lobby-music-play-btn"
        >
          <Play className="w-4 h-4" /> Play music
        </Button>
      ) : (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={() => setMuted((m) => !m)}
            aria-label={silent ? "Unmute music" : "Mute music"}
            title={silent ? "Unmute music" : "Mute music"}
            data-testid="lobby-music-mute-btn"
          >
            <Icon className="w-4 h-4" />
          </Button>
          <Slider
            value={[silent ? 0 : Math.round(volume * 100)]}
            max={100}
            step={1}
            className="w-24"
            aria-label="Music volume"
            onValueChange={([v]) => {
              setVolume(v / 100);
              if (v > 0 && muted) setMuted(false);
            }}
            data-testid="lobby-music-volume-slider"
          />
        </>
      )}
    </div>
  );
};
